import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {
  PRODUCT_CATEGORIES,
  createCustomProduct
} from './product-catalogue.js';

export const CUSTOM_PRODUCT_ENTITY_TYPE='custom_product';

function required(value,label,max=160){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function idOrGenerated(value){
  const id=String(value||'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

function clean(row){
  const copy={...row};
  delete copy.key;
  delete copy.entityType;
  return copy;
}

function assertCategory(value){
  const key=String(value||'other').trim().toLowerCase();
  if(!PRODUCT_CATEGORIES.includes(key))throw new TypeError('Invalid product category');
  return key;
}

function withExisting(existing,product){
  if(!existing){
    return {
      ...createEntityMeta({id:product.id,syncStatus:SyncStatus.PENDING_CREATE}),
      ...product
    };
  }
  return {
    ...clean(existing),
    ...product,
    id:existing.id,
    userId:existing.userId
  };
}

export function createCustomProductStore({
  repository,
  queue,
  userId,
  now=()=>new Date().toISOString()
}={}){
  if(!repository?.get||!repository?.list||!repository?.put)throw new TypeError('repository with get/list/put is required');
  if(!queue?.enqueue||!queue?.markConflict)throw new TypeError('sync queue is required');
  const owner=required(userId,'userId',128);

  function assertOwner(row){
    if(row?.userId!==owner)throw new Error('CUSTOM_PRODUCT_OWNER_MISMATCH');
    return row;
  }

  async function get(id){
    const row=await repository.get(CUSTOM_PRODUCT_ENTITY_TYPE,required(id,'id',128));
    if(!row)return null;
    return Object.freeze(assertOwner(row));
  }

  async function list({includeArchived=false}={}){
    const rows=await repository.list(CUSTOM_PRODUCT_ENTITY_TYPE);
    return Object.freeze((rows||[])
      .filter(row=>row.userId===owner)
      .filter(row=>includeArchived||!row.archivedAt)
      .sort((a,b)=>String(a.name||'').localeCompare(String(b.name||''),'pl')));
  }

  async function create(input={}){
    const id=idOrGenerated(input.id);
    const product=createCustomProduct({
      id,
      userId:owner,
      name:input.name,
      categoryKey:assertCategory(input.categoryKey),
      archivedAt:null
    });
    const entity={
      ...withExisting(null,product),
      createdAt:now(),
      updatedAt:now()
    };
    return queue.enqueue(CUSTOM_PRODUCT_ENTITY_TYPE,entity,'create');
  }

  async function update(id,changes={}){
    const current=await get(id);
    if(!current)throw new Error('CUSTOM_PRODUCT_NOT_FOUND');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new Error('CUSTOM_PRODUCT_CONFLICT');
    const product=createCustomProduct({
      id:current.id,
      userId:owner,
      name:changes.name??current.name,
      categoryKey:changes.categoryKey??current.categoryKey,
      archivedAt:current.archivedAt
    });
    const entity={...withExisting(current,product),updatedAt:now()};
    const operation=Number(current.serverVersion)>0?'update':'create';
    return queue.enqueue(CUSTOM_PRODUCT_ENTITY_TYPE,entity,operation);
  }

  async function archive(id,{at=now()}={}){
    const current=await get(id);
    if(!current)throw new Error('CUSTOM_PRODUCT_NOT_FOUND');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new Error('CUSTOM_PRODUCT_CONFLICT');
    const product=createCustomProduct({
      id:current.id,
      userId:owner,
      name:current.name,
      categoryKey:current.categoryKey,
      archivedAt:at
    });
    const entity={...withExisting(current,product),updatedAt:at};
    const operation=Number(current.serverVersion)>0?'update':'create';
    return queue.enqueue(CUSTOM_PRODUCT_ENTITY_TYPE,entity,operation);
  }

  async function markConflict(id,details){
    const current=await get(id);
    if(!current)throw new Error('CUSTOM_PRODUCT_NOT_FOUND');
    return queue.markConflict(CUSTOM_PRODUCT_ENTITY_TYPE,current,details);
  }

  return Object.freeze({get,list,create,update,archive,markConflict});
}
